'use client';

import AppImage from '@/components/ui/AppImage';

interface OurStoryProps {
  className?: string;
}

const OurStory = ({ className = '' }: OurStoryProps) => {
  return (
    <section className={`relative py-12 overflow-hidden ${className}`}>
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/Revive_in_Hospital_5-1766903195945.jpeg"
          alt="Neonatal care unit with REVIVE system"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-blue-800/65 to-indigo-900/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-12 lg:p-16 shadow-2xl border border-white/20">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="md:text-5xl lg:text-6xl font-bold text-white mb-4 text-[32px]">
              Our Story
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-indigo-400 mx-auto rounded-full"></div>
          </div>

          {/* Story Content */}
          <div className="space-y-6 text-white/95 text-base md:text-lg leading-relaxed">
            <p className="text-xl md:text-2xl font-light italic text-white/90">
              Every life begins with a moment of hope. But for some newborns, those first moments are fragile—shaped by complications they cannot fight on their own.
            </p>

            <p>
              Sensivision Health Technologies Pvt. Ltd. was born from a simple yet powerful belief: no baby should lose their future because life-saving care was unavailable, unaffordable, or too complex to deliver.
            </p>

            <p>
              In neonatal intensive care units, clinicians face heart-wrenching decisions every day. When a newborn suffers a lack of oxygen at birth, minutes matter. The right intervention, delivered at the right time, can mean the difference between a healthy childhood and a lifetime of challenges. Yet proven treatments often remain out of reach for many hospitals.
            </p>

            <p className="font-semibold text-white">
              This is where Sensivision Health Technologies Pvt. Ltd. began.
            </p>

            <p>
              Our first step was REVIVE, a therapeutic hypothermia system created to bring world-class neonatal care to settings where it was needed most. Designed hand-in-hand with doctors and nurses, REVIVE was built to be reliable, intuitive, and affordable—without compromising on safety or outcomes. Each baby treated reaffirmed our purpose.
            </p>

            <p className="font-semibold text-white">
              But saving lives is only the beginning.
            </p>

            <p>
              Today, Sensivision Health Technologies Pvt. Ltd. is moving toward a future where intelligence augments care—where subtle warning signs are detected earlier, where clinicians are supported by data-driven insights, and where every newborn benefits from the collective learning of many hospitals. Through AI-enabled monitoring and connected care platforms, we aim to give caregivers more clarity, confidence, and time—when it matters most.
            </p>

            <p className="text-xl md:text-2xl font-semibold text-white italic">
              We are guided by a simple promise: technology should serve compassion, not replace it.
            </p>

            <p>
              As we look ahead, our mission remains unchanged—to ensure that every newborn, no matter where they are born, has a fighting chance at a healthy life.
            </p>
          </div>

          {/* Decorative Element */}
          <div className="mt-12 flex justify-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent via-blue-900/30 to-blue-50 dark:to-blue-950 z-10" />
    </section>);

};

export default OurStory;